import { type TimestampShared } from "../../../functions/utils/sharedDataTypes";
import { type UserAgreement } from "../../../legal/userAgreement";
import { MediaType } from "../../collections";

export type {
  UserAgreement,
  UserAgreementHistoryEntry,
  LegalAgreementVersion,
  UserAgreementStatus,
  UserAgreementMethod,
} from "../../../legal/userAgreement";

/**
 * `users/{uid}` — root user document. Subcollections live next to this file in
 * sibling folders that mirror the Firestore tree:
 * - `activityLog/`   → `users/{uid}/activityLog/{logId}`
 * - `favorites/`     → `users/{uid}/favorites/{favoriteId}`
 * - `watchedFiles/`  → `users/{uid}/watchedFiles/{mediaId}`
 * - `gamification/`  → `users/{uid}/gamification/stats`
 * - `internal/`      → `users/{uid}/internal/...` (server-only)
 */
export interface UserDocument {
  onboarded?: boolean;
  userAgreement?: UserAgreement;
  userAgreementTracking?: UserAgreementTracking;
  questionnaire?: Questionnaire; // We not populate this anymore since Release 1.8.2
  introCourseState?: IntroCourseState;
  created_on?: TimestampShared;
  /** Bumped on every client write to the user root document (enforced in Firestore rules). */
  updated_on?: TimestampShared;
  recommendations?: Recommendation[];
}

interface UserAgreementTracking {
  active: boolean;
  currentVersion: number;
  history: UserAgreementTrackingItems[];
}

export interface UserAgreementTrackingItems {
  active: boolean;
  version: number;
  timestamp: TimestampShared;
}

export interface Questionnaire {
  goal: Goal;
  experience: number;
  currentRelaxationLevel: number;
  foundOn: FoundOn;
}

export enum Goal {
  EntspannungLernen = "Entspannung lernen",
  BesserSchlafen = "Besser Schlafen",
  RegelmäßigEntspannen = "regelmäßig entspannen",
  AngstUndPanikÜberwinden = "Angst und Panik überwinden",
  StressAbbauen = "Stress abbauen",
}

export enum FoundOn {
  SocialMedia = "Social Media",
  Newsletter = "Newsletter",
  Empfehlung = "Empfehlung",
  Google = "Google",
  Stores = "App Store oder Play Store",
  Anders = "Was anderes",
}

export enum IntroCourseState {
  Watched = "Watched",
  Dismissed = "Dismissed",
}

export interface Recommendation {
  mediaType: MediaType.AudioCourses | MediaType.MeditationDailies;
  id: string;
  created_on: TimestampShared;
  createdBy: "purchase" | "registration" | "webButton" | "adminTool" | "landingCampaignPromo";
}

// This one is different to UserDocument since we do not need
// all the fields for the user in the app context
export interface UserContext {
  role: UserRole;
  userFetchedAt?: TimestampShared;
  creationTimeTimestamp?: string;
  onboarded: boolean;
  introCourseState?: IntroCourseState;
  trackingConsent: boolean;
  userAgreement?: UserAgreement;
  loadingUser: boolean;
  recommendations?: Recommendation[];
}

export enum UserRole {
  Base = "Base",
  Pro = "Pro",
}

export interface FirebaseUserClaims {
  role?: UserRole;
  admin?: boolean;
}
